import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import KPICard from '../components/KPICard';
import { useAuthFetch } from '../hooks/useAuthFetch';

const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '—';

const ETAPE_COLORS = { Qualification: '#41B073', Proposition: '#3b82f6', Négociation: '#f59e0b', Gagnée: '#22c55e', Perdue: '#ef4444' };
const STATUT_COLORS = { Prospect: '#41B073', 'Client actif': '#22c55e', 'Client inactif': '#94a3b8', Perdu: '#ef4444' };

const Card = ({ children, style }) => (
  <div style={{ background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 10, padding: 20, ...style }}>{children}</div>
);
const ChartTitle = ({ children }) => (
  <div style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{children}</div>
);

const ACTIVITE_ROUTES = { 'Devis': '/devis', 'Facture': '/factures', 'Contrat': '/contrats', 'Opportunité': '/opportunites', 'Client': '/clients' };

export default function DashboardPage() {
  const fetchWithAuth = useAuthFetch();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [evolution, setEvolution] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [statutsClients, setStatutsClients] = useState([]);
  const [activite, setActivite] = useState([]);
  const [renouvellements, setRenouvellements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [k, e, p, t, s, a, r] = await Promise.all([
          fetchWithAuth('/api/dashboard/kpis').then(r => r.json()),
          fetchWithAuth('/api/dashboard/evolution').then(r => r.json()),
          fetchWithAuth('/api/dashboard/pipeline').then(r => r.json()),
          fetchWithAuth('/api/dashboard/top_clients').then(r => r.json()),
          fetchWithAuth('/api/dashboard/statuts_clients').then(r => r.json()),
          fetchWithAuth('/api/dashboard/activite_recente').then(r => r.json()),
          fetchWithAuth('/api/dashboard/renouvellements').then(r => r.json()),
        ]);
        setKpis(k); setEvolution(e); setPipeline(p); setTopClients(t);
        setStatutsClients(s); setActivite(a); setRenouvellements(r);
      } catch { }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div style={{ padding: 40, color: '#94a3b8' }}>Chargement...</div>;

  const tooltipStyle = { background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 8, color: '#e2e8f0', fontSize: 13 };

  return (
    <div style={{ padding: 28, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>Tableau de bord</h1>
        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>
          {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* KPI Row */}
      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        <KPICard title="Clients actifs" value={kpis?.clients_actifs ?? '—'} subtitle={`+${kpis?.nouveaux_clients_30j || 0} ce mois`} color="#22c55e" icon="👥" onClick={() => navigate('/clients')} />
        <KPICard title="MRR contrats" value={fmt(kpis?.mrr)} subtitle="Revenus récurrents mensuels" color="#41B073" icon="🔄" onClick={() => navigate('/contrats')} />
        <KPICard title="Pipeline pondéré" value={fmt(kpis?.valeur_pipeline)} subtitle={`${kpis?.total_opportunites || 0} opportunité(s)`} color="#3b82f6" icon="💼" onClick={() => navigate('/opportunites')} />
        <KPICard title="CA facturé (30j)" value={fmt(kpis?.ca_facture_30j)} subtitle="Factures payées" color="#f59e0b" icon="💶" onClick={() => navigate('/factures')} />
        <KPICard title="Factures en retard" value={kpis?.factures_en_retard || 0} subtitle="À relancer" color="#ef4444" icon="⚠️" alert={(kpis?.factures_en_retard || 0) > 0} onClick={() => navigate('/factures')} />
      </div>

      {/* Renouvellements contrats */}
      {renouvellements.length > 0 && (
        <Card style={{ marginBottom: 16, borderLeft: '4px solid #f59e0b' }}>
          <ChartTitle>⏰ Contrats à renouveler — 60 prochains jours ({renouvellements.length})</ChartTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
            {renouvellements.map(c => (
              <div key={c.id} onClick={() => navigate('/contrats')} style={{
                background: '#12141f', borderRadius: 8, padding: '12px 14px', cursor: 'pointer',
                borderLeft: `3px solid ${c.jours_restants <= 14 ? '#ef4444' : c.jours_restants <= 30 ? '#f97316' : '#f59e0b'}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', flex: 1, marginRight: 8 }}>{c.titre}</div>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap',
                    background: c.jours_restants <= 14 ? '#ef444422' : c.jours_restants <= 30 ? '#f9731622' : '#f59e0b22',
                    color: c.jours_restants <= 14 ? '#ef4444' : c.jours_restants <= 30 ? '#f97316' : '#f59e0b',
                  }}>
                    J-{c.jours_restants}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{c.client_nom} · {fmt(c.montant_ht)}/mois</div>
                <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
                  Échéance : {fmtDate(c.date_fin)}
                  {c.renouvellement_auto ? ' · 🔄 Auto' : ' · ⚠️ Manuel'}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Charts Row 1 */}
      <div className="chart-grid">
        <Card>
          <ChartTitle>Évolution du CA (12 mois)</ChartTitle>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={evolution}>
              <XAxis dataKey="mois" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${Math.round(v / 1000)}k€`} />
              <Tooltip contentStyle={tooltipStyle} formatter={v => fmt(v)} cursor={{ stroke: '#41B07340', strokeWidth: 1 }} />
              <Line type="monotone" dataKey="ca" stroke="#41B073" strokeWidth={2.5} dot={{ r: 3, fill: '#41B073' }} name="CA" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <ChartTitle>Pipeline par étape</ChartTitle>
          <ResponsiveContainer width="100%" height={200} style={{ overflow: 'visible' }}>
            <BarChart data={pipeline} layout="vertical">
              <XAxis type="number" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${Math.round(v / 1000)}k€`} />
              <YAxis dataKey="etape" type="category" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} width={95} />
              <Tooltip contentStyle={tooltipStyle} formatter={v => fmt(v)} cursor={{ fill: '#ffffff08' }} />
              <Bar dataKey="valeur" radius={[0, 4, 4, 0]} name="Valeur" style={{ cursor: 'pointer' }} onClick={(data) => navigate('/opportunites', { state: { etape: data.etape } })}>
                {pipeline.map((entry, i) => <Cell key={i} fill={ETAPE_COLORS[entry.etape] || '#41B073'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="chart-grid">
        <Card>
          <ChartTitle>Répartition clients par statut</ChartTitle>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart margin={{ top: 24, right: 24, bottom: 24, left: 24 }}>
              <Pie data={statutsClients} dataKey="count" nameKey="statut" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3}
                label={({ statut, percent }) => `${statut} (${(percent * 100).toFixed(0)}%)`} labelLine={{ stroke: '#2a2d3e' }}
                style={{ cursor: 'pointer' }} onClick={() => navigate('/clients')}>
                {statutsClients.map((entry, i) => <Cell key={i} fill={STATUT_COLORS[entry.statut] || '#41B073'} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} cursor={false} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <ChartTitle>Top clients (CA total)</ChartTitle>
          <ResponsiveContainer width="100%" height={220} style={{ overflow: 'visible' }}>
            <BarChart data={topClients} layout="vertical">
              <XAxis type="number" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${Math.round(v / 1000)}k€`} />
              <YAxis dataKey="nom" type="category" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} width={130} />
              <Tooltip contentStyle={tooltipStyle} formatter={v => fmt(v)} cursor={{ fill: '#ffffff08' }} />
              <Bar dataKey="ca_total" fill="#41B073" radius={[0, 4, 4, 0]} name="CA total" style={{ cursor: 'pointer' }} onClick={(data) => navigate('/clients', { state: { clientId: data.id } })} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Activité récente */}
      <Card>
        <ChartTitle>Activité récente</ChartTitle>
        {activite.length === 0 ? (
          <div style={{ color: '#64748b', fontSize: 13 }}>Aucune activité récente</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {activite.map((item, i) => (
              <div key={i} onClick={() => navigate(ACTIVITE_ROUTES[item.type] || '/dashboard')}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: '#0f111780', borderRadius: 8, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = '#41B07315'}
                onMouseLeave={e => e.currentTarget.style.background = '#0f111780'}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.titre}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    {item.type} · {item.statut}
                    {item.date ? ` · ${new Date(item.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                  </div>
                </div>
                {item.montant > 0 && (
                  <div style={{ fontSize: 13, color: '#41B073', fontWeight: 600, fontFamily: 'DM Mono, monospace', flexShrink: 0 }}>{fmt(item.montant)}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
